import React, { useState, useEffect, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import StockApiDocs from './pages/StockApiDocs';
import HomePageVeteralia from './pages/HomePageVeteralia';
import LoginPage from './pages/LoginPage'; 
import NuevoPedido from './pages/NuevoPedido';
import FinalizarPedido from './pages/FinalizarPedido'; 
import GestionPedidos from './pages/GestionPedidos'; 
import DetallePedido from './pages/DetallePedido'; 
import PedidosCerrados from './pages/PedidosCerrados';
import AddProduct from './pages/AddProduct';
import NewUser from './pages/NewUser';
import UserList from './pages/UsersList';
import NewClient from './pages/NewClient';
import ClientsList from './pages/ClientsList';
import './index.css';
import UpdateProducts from './pages/UpdateProducts';
import NewProvider from './pages/NewProvider';
import Licitaciones from './pages/Licitaciones';
import ProductosLicitacion from './pages/ProductosLicitacion';
import ClientManagementWrapper from './pages/ClientManagementWrapper';
import AdminApis from './pages/AdminApis';
import MisApiKeys from './pages/MisApiKeys';
import RegisterContactPage from './pages/RegisterContactPage';
import AnalisisProveedores from './pages/ProveedorAnalytics';
import ProviderManagement from './pages/ProviderManagement';
import GestionOfertas from './pages/GestionOfertas';
import GestionOfertasAdmin from './pages/GestionOfertasAdmin';
import GestionCitas from './pages/AppointmentManagement';
import CalendarioPublico from './pages/CalendarioIframe';
import CalendarioEmbedido from './pages/CalendarioEmbedido';
import { ThemeProvider, ThemeContext } from './contexts/ThemeContext';
import Incidencias from './pages/Incidencias';
import AhorrosWrapper from './pages/AhorrosWrapper';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import BlogAdmin from './pages/BlogAdmin';
import PreciosGrupo from './pages/PreciosGrupo';
import GestionStock from './pages/GestionStock';
import OperacionesConsumo from './pages/OperacionesConsumo';
import PrivacidadApp from './pages/PrivacidadApp';
import SoporteApp from './pages/SoporteApp';


const API_URL = process.env.REACT_APP_API_URL;

// Dominios que usan la versión Veteralia (solo login, sin landing)
const CLIENT_DOMAINS = [
  'centralsaludfront-production-d227.up.railway.app',
];

const isVeteraliaDomain = CLIENT_DOMAINS.includes(window.location.hostname);

// Contexto de autenticación
export const AuthContext = createContext();

// Ruta protegida
// `denyPermisos` (opcional): lista de permisos a los que se les NIEGA el acceso
// a esta ruta (se los redirige a `redirectTo`). Ej: denyPermisos={[4]} bloquea
// a los proveedores. El resto de roles sigue entrando normal.
const ProtectedRoute = ({ children, denyPermisos, redirectTo = "/" }) => {
  const { isAuthenticated, loading, user } = React.useContext(AuthContext);
  const { themeReady } = React.useContext(ThemeContext);

  if (loading || !themeReady) {
    return (
      <div className="flex justify-center items-center h-screen bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-gray-500 animate-spin" />
          <span className="text-gray-400 text-sm">Cargando...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  if (denyPermisos && user && denyPermisos.includes(user.permisos)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

// Placeholder para páginas en construcción
const PlaceholderPage = ({ title }) => (
  <div className="flex justify-center items-center h-[calc(100vh-12rem)] bg-gray-50">
    <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold text-teal-600 mb-4">{title}</h1>
      <p className="text-gray-600">
        Esta página está en construcción. Pronto estará disponible con todas sus funcionalidades.
      </p>
    </div>
  </div>
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const timeoutId = setTimeout(() => {
      console.log('Verificación de token excedió el tiempo límite. Abortando...');
      controller.abort();
      setLoading(false);
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      setUser(null);
    }, 10000);

    const token = localStorage.getItem('token');

    if (token) {
      fetch(`${API_URL}/api/user/me`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      })
        .then((response) => {
          if (response.ok) return response.json();
          throw new Error('Token inválido');
        })
        .then((data) => {
          setUser(data.user);
          setIsAuthenticated(true);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          setUser(null);
        })
        .finally(() => {
          clearTimeout(timeoutId);
          setLoading(false);
        });
    } else {
      clearTimeout(timeoutId);
      setLoading(false);
    }

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
  };

  const HomeComponent = isVeteraliaDomain ? HomePageVeteralia : HomePage;

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, loading }}>
      <ThemeProvider>
        <Router>
          <Routes>
            {/* Ruta pública del calendario – sin layout ni autenticación */}
            <Route
              path="/calendario-publico/:token"
              element={<CalendarioPublico />}
            />

            {/* Documentación de la API de Stock. Es pública: los integradores
                del cliente necesitan poder leerla sin tener cuenta CentralSalud.
                No se enlaza desde menús; se accede por URL directa o desde la
                sección "API Keys" del panel del cliente. */}
            <Route path="/stock-api-docs" element={<StockApiDocs />} />
            {/* Vistas separadas: cada una muestra su API + el tronco común */}
            <Route path="/api-docs/stock"  element={<StockApiDocs api="stock" />} />
            <Route path="/api-docs/datos"  element={<StockApiDocs api="datos" />} />

            {/* Páginas públicas para la ficha de la App Store (Apple): privacidad
                y soporte. Sin layout ni autenticación; se acceden por URL directa. */}
            <Route path="/privacidad-app" element={<PrivacidadApp />} />
            <Route path="/soporte-app" element={<SoporteApp />} />

            {/* Resto de rutas con Layout */}
            <Route
              path="/*"
              element={
                <Layout>
                  <Routes>
                    <Route path="/" element={<HomeComponent />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterContactPage />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route
                      path="/blog/admin"
                      element={
                        <ProtectedRoute>
                          <BlogAdmin />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/blog/:slug" element={<BlogPost />} />

                    <Route
                      path="/gestion-clientes"
                      element={
                        <ProtectedRoute>
                          <ClientManagementWrapper />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin-apis"
                      element={
                        <ProtectedRoute>
                          <AdminApis />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/mis-api-keys"
                      element={
                        <ProtectedRoute>
                          <MisApiKeys />
                        </ProtectedRoute>
                      }
                    />

                    {/* /ahorros → AhorrosWrapper decide si mostrar admin o cliente */}
                    <Route
                      path="/ahorros"
                      element={
                        <ProtectedRoute>
                          <AhorrosWrapper />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/nuevo-pedido"
                      element={
                        <ProtectedRoute>
                          <NuevoPedido />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/analisis"
                      element={
                        <ProtectedRoute denyPermisos={[4]} redirectTo="/gestion-clientes">
                          <AnalisisProveedores />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/gestion-citas"
                      element={
                        <ProtectedRoute>
                          <GestionCitas />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/finalizar-pedido"
                      element={
                        <ProtectedRoute>
                          <FinalizarPedido />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/gestion-clientes/nuevo"
                      element={
                        <ProtectedRoute>
                          <PlaceholderPage title="Nuevo Cliente" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/gestion-clientes/editar"
                      element={
                        <ProtectedRoute>
                          <PlaceholderPage title="Ver/Editar Clientes" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/usuarios"
                      element={
                        <ProtectedRoute>
                          <UserList title="Listado de Usuarios" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/usuarios/nuevo"
                      element={
                        <ProtectedRoute>
                          <NewUser title="Nuevo Usuario" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/clientes/nuevo"
                      element={
                        <ProtectedRoute>
                          <NewClient title="Nuevo Cliente" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/proveedores/nuevo"
                      element={
                        <ProtectedRoute>
                          <NewProvider title="Nuevo Proveedor" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/licitaciones"
                      element={
                        <ProtectedRoute>
                          <Licitaciones title="Licitaciones" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/incidencias"
                      element={
                        <ProtectedRoute>
                          <Incidencias title="Incidencias" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/licitaciones/:licitacionId/productos"
                      element={
                        <ProtectedRoute>
                          <ProductosLicitacion />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/clientes/listado"
                      element={
                        <ProtectedRoute>
                          <ClientsList title="Listado de Clientes" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/gestion-ofertas"
                      element={
                        <ProtectedRoute>
                          <GestionOfertas />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/gestion-ofertas-admin"
                      element={
                        <ProtectedRoute>
                          <GestionOfertasAdmin />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/gestion-pedidos"
                      element={
                        <ProtectedRoute>
                          <GestionPedidos />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/pedidos/detalles/:pedidoId"
                      element={
                        <ProtectedRoute>
                          <DetallePedido />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/add-product"
                      element={
                        <ProtectedRoute>
                          <AddProduct />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/update-products"
                      element={
                        <ProtectedRoute>
                          <UpdateProducts />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/pedidos/cerrados"
                      element={
                        <ProtectedRoute>
                          <PedidosCerrados />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/gestion-proveedores"
                      element={
                        <ProtectedRoute>
                          <ProviderManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/calendario-embedido"
                      element={
                        <ProtectedRoute>
                          <CalendarioEmbedido />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/precios"
                      element={
                        <ProtectedRoute>
                          <PreciosGrupo />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/gestion-stock"
                      element={
                        <ProtectedRoute>
                          <GestionStock />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/operaciones"
                      element={
                        <ProtectedRoute>
                          <OperacionesConsumo />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Layout>
              }
            />
          </Routes>
        </Router>
      </ThemeProvider>
    </AuthContext.Provider>
  );
}

export default App;